// --------------------------------------------------------------
// Created On: 2022-06-21
// Author: Zachary Thomas
//
// Last Modified: 2025-11-04
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React from "react";
import formatDateShortLocal from "../../../../utilities/time/formatDateShortLocal";
import IconTooltip from "../../../../components/IconTooltip/IconTooltip";
import deepCopy from "src/utilities/deepCopy";
import styles from "./HistoryTable.module.scss";

// Table for viewing historical data points.
export default function HistoryTable(props: Props): Component {
  // Returns the local timestamp string from unix timestamp.
  function getLocalTimestampFromUnixTimestamp(unixTimestamp: number): string {
    const date = new Date(unixTimestamp);
    return formatDateShortLocal(date.toISOString().split(".")[0]).replace(",", "");
  }

  // Depending on if we are supposed to show all data points or not, filters the data points to only include changes.
  // If two data points have the same value, then only the oldest one is returned.
  // We also reverse the order of the array so newer values show at the top.
  function filterAndFormatDataPoints(
    dataPoints: [number, number | string][],
    showAllLogs: boolean
  ): [number, number | string][] {
    const dataPointsDeepCopy = deepCopy(dataPoints);
    let newDataPoints: [number, number | string][] = [];
    if (showAllLogs) {
      newDataPoints = dataPointsDeepCopy;
    } else {
      let previousValue: number | string | null = null;
      newDataPoints = dataPointsDeepCopy.filter((dataPoint) => {
        if (dataPoint[1] === previousValue) {
          return false;
        } else {
          previousValue = dataPoint[1];
          return true;
        }
      });
    }
    return newDataPoints.reverse();
  }

  return (
    <div className={`${styles.body} my-3`} data-test="data-history-table">
      {props.series.map((singleSeries) => (
        <table key={singleSeries.name} className={`${styles.table} table`}>
          <thead className={styles.thead}>
            <tr>
              <th className={styles.th}>{singleSeries.name}</th>
              <th className={styles.th}>Date</th>
              <th className={styles.th} />
            </tr>
          </thead>
          <tbody>
            {filterAndFormatDataPoints(singleSeries.data, props.showAllLogs).map((singleDataPoint, i) => (
              <tr key={i}>
                <td>
                  {singleDataPoint[1]} {singleSeries.unitShortName !== null && singleSeries.unitShortName}{" "}
                  {`${singleDataPoint[0]}` in singleSeries.dataMarkerMap && (
                    <IconTooltip
                      id={`stale-data-tooltip-${i}`}
                      icon="exclamation-triangle"
                      message={"This data was uploaded after network communication was restored."}
                      color="var(--bs-warning)"
                    />
                  )}
                </td>
                <td>{getLocalTimestampFromUnixTimestamp(singleDataPoint[0])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ))}
    </div>
  );
}

interface Props {
  series: FlexibleTimeDataSeries[];
  showAllLogs: boolean;
}

interface FlexibleTimeDataSeries {
  name: string;
  unitName: string;
  unitShortName: string;
  data: [number, number | string][];
  dataMarkerMap: DataMarkerMap;
}

interface DataMarkerMap {
  [key: string]: DataMarker;
}

interface DataMarker {
  value: number | string;
}
